import { useState, type FC } from "react";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "@privacy/translations";
import {
  responsibleAccessErrorWithCode,
  ResponsibleAccessErrorCodes,
  type ConnectedTenant,
  type RegistrationById,
} from "../../../api/registrations.types";
import { copyRegistrationMutationOptions } from "../../../api/registrations";
import { trackEvent } from "../../../shared/Snowplow";
import * as FormDialog from "../../../shared/FormDialog";
import { CreatableTenantsSelect, useCreatableTenants } from "../CreatableTenantsSelect";
import { useRegistrationVariant } from "../RegistrationVariantProvider";
import { ResponsibleLosesAccessDialog } from "./ResponsibleLosesAccessDialog";

type CopyRegistrationDialogProps = {
  registration: RegistrationById;
  onClose: () => void;
  onCopied: (newRegistrationId: string) => void;
};

export const CopyRegistrationDialog: FC<CopyRegistrationDialogProps> = ({ registration, onClose, onCopied }) => {
  const { t, formatList } = useTranslation();
  const variant = useRegistrationVariant();
  const copyRegistrationMutation = useMutation(copyRegistrationMutationOptions(variant, registration.id));
  const { creatableTenants, takesOneUnit } = useCreatableTenants();

  const [pickedTenants, setPickedTenants] = useState<Array<ConnectedTenant> | null>(null);
  const [unitsResponsibleCannotAccess, setUnitsResponsibleCannotAccess] = useState<Array<string> | null>(null);

  const copyableOriginTenants = registration.connectedTenants.filter(tenant =>
    creatableTenants.some(creatable => creatable.id === tenant.id)
  );

  const selectedTenants = pickedTenants ?? copyableOriginTenants;
  const emptiedByUser = pickedTenants?.length === 0;

  const cannotBeResponsibleError = responsibleAccessErrorWithCode(
    copyRegistrationMutation.error,
    ResponsibleAccessErrorCodes.youCannotBeResponsibleInEveryTenant
  );

  const copy = (isConfirm: boolean) =>
    copyRegistrationMutation.mutate(
      {
        name: `${registration.name} ${t("RegistrationDrawer.Registration.Copy")}`,
        tenantIds: selectedTenants.map(tenant => tenant.id),
        isConfirm,
      },
      {
        onSuccess: registrationCopy => onCopied(registrationCopy.id),
        onError: error => {
          const responsibleHasNoAccessError = responsibleAccessErrorWithCode(
            error,
            ResponsibleAccessErrorCodes.responsibleHasNoAccessToNewTenant
          );
          if (responsibleHasNoAccessError) {
            setUnitsResponsibleCannotAccess(responsibleHasNoAccessError.tenantsResponsibleCannotAccess);
            copyRegistrationMutation.reset();
            return;
          }

          setUnitsResponsibleCannotAccess(null);
        },
      }
    );

  const changeTenants = (tenants: Array<ConnectedTenant>) => {
    setPickedTenants(tenants);

    if (copyRegistrationMutation.isError) copyRegistrationMutation.reset();
  };

  if (unitsResponsibleCannotAccess) {
    return (
      <ResponsibleLosesAccessDialog
        context="copy"
        registrationName={registration.name}
        responsibleName={`${registration.responsible.firstName} ${registration.responsible.lastName}`}
        unitsResponsibleCannotAccess={unitsResponsibleCannotAccess}
        isSubmitting={copyRegistrationMutation.isPending}
        onConfirm={() => copy(true)}
        onCancel={() => setUnitsResponsibleCannotAccess(null)}
      />
    );
  }

  return (
    <FormDialog.Dialog isOpen={true}>
      <FormDialog.Title>{t("RegistrationDrawer.Copy.DialogHeader")}</FormDialog.Title>

      <FormDialog.Form
        onSubmit={event => {
          event.preventDefault();
          trackEvent.copyRegistrationClick();
          copy(false);
        }}
      >
        <FormDialog.Content>
          <FormDialog.FormError
            isError={copyRegistrationMutation.isError}
            errorMessage={
              cannotBeResponsibleError
                ? t("RegistrationDrawer.Copy.YouCannotBeResponsibleError", {
                    units: formatList(cannotBeResponsibleError.tenantsResponsibleCannotAccess),
                  })
                : undefined
            }
          />
          <FormDialog.Fields>
            <FormDialog.Field
              errorMessage={
                emptiedByUser
                  ? t(
                      takesOneUnit
                        ? "CreateRegistration.Dialog.TenantError.One"
                        : "CreateRegistration.Dialog.TenantError.Several"
                    )
                  : undefined
              }
            >
              <FormDialog.Label htmlFor="copyTenants">
                {t(
                  takesOneUnit
                    ? "CreateRegistration.Dialog.TenantsLabel.One"
                    : "CreateRegistration.Dialog.TenantsLabel.Several"
                )}
              </FormDialog.Label>
              <CreatableTenantsSelect
                id="copyTenants"
                selectedTenants={selectedTenants}
                onChange={changeTenants}
                error={emptiedByUser}
              />
            </FormDialog.Field>
          </FormDialog.Fields>
        </FormDialog.Content>

        <FormDialog.ActionButtons>
          <FormDialog.CancelButton onClick={onClose} />
          <FormDialog.SubmitButton
            isSubmitting={copyRegistrationMutation.isPending}
            isDisabled={selectedTenants.length === 0}
          >
            {t("RegistrationDrawer.Copy.Label")}
          </FormDialog.SubmitButton>
        </FormDialog.ActionButtons>
      </FormDialog.Form>
    </FormDialog.Dialog>
  );
};
